import api from '@/lib/api/axios'

/**
 * The Recovery Ticker's REST surface, coded to the Phase 2 contract in docs/ticker-design.md §5.1.
 *
 * Every response is the app's standard envelope, `{ success: true, data, message, timestamp }`,
 * and every id is a JSON number. All of these require `ticker.view` on the server, except the
 * preferences, which only need a signed-in user.
 */

export const TICKER_KINDS = [
    'task_complete',
    'object_complete',
    'task_issue',
    'engineer_update',
    'management_update',
    'rta_change',
] as const
export type TickerKind = (typeof TICKER_KINDS)[number]

export type TickerLevel = 'detail' | 'high' | 'mgmt'
export const TICKER_LEVELS: readonly TickerLevel[] = ['detail', 'high', 'mgmt']

/** How long each card stays on the bar: a multiplier on both dwell classes (kinds.ts). */
export type TickerDwell = 'quick' | 'normal' | 'slow'
export const TICKER_DWELLS: readonly TickerDwell[] = ['quick', 'normal', 'slow']

export interface TickerPrefs {
    enabled: boolean
    level: TickerLevel
    /**
     * "All recovery sets" (Andrew, 29 Sep 2026): the user receives every OPEN set, including sets
     * opened later. The server resolves it: on every read `recoverySetIds` is the sets in effect
     * (every open one), so everything that watches, backfills or filters keeps working off
     * `recoverySetIds` alone. normalizePrefs always fills it; it is optional only so that a value
     * written without it still types.
     */
    allSets?: boolean
    recoverySetIds: number[]
    dwell: TickerDwell
}

/**
 * The most sets one watch or one ?sets= read takes (the server's MAX_SETS), and so the most a
 * chosen list can hold.
 */
export const TICKER_MAX_SETS = 50

/**
 * The server's defaults when the user has saved nothing (§5.1): All recovery sets. The server
 * answers with every open set in `recoverySetIds`; this stands in only until it has.
 */
export const DEFAULT_TICKER_PREFS: TickerPrefs = { enabled: true, level: 'high', allSets: true, recoverySetIds: [], dwell: 'normal' }

/**
 * Preferences as the server sent them, made whole. Prefs saved before `dwell` existed read back
 * without it (the server fills 'normal', and so does this, in case it meets an older server).
 * `allSets` is the server's to decide - it reads an old saved value that ticked nothing as All -
 * so a value without it here is taken as the list it carries.
 */
export function normalizePrefs(raw: Partial<TickerPrefs> | null | undefined): TickerPrefs {
    const p = raw ?? {}
    return {
        enabled: typeof p.enabled === 'boolean' ? p.enabled : DEFAULT_TICKER_PREFS.enabled,
        level: TICKER_LEVELS.includes(p.level as TickerLevel) ? (p.level as TickerLevel) : DEFAULT_TICKER_PREFS.level,
        allSets: p.allSets === true,
        recoverySetIds: Array.isArray(p.recoverySetIds) ? p.recoverySetIds.filter((id) => typeof id === 'number') : [],
        dwell: TICKER_DWELLS.includes(p.dwell as TickerDwell) ? (p.dwell as TickerDwell) : 'normal',
    }
}

/**
 * What a save sends. On All the list goes empty: the server stores none and answers with the sets
 * in effect. The client's copy holds every open set, and sending that would trip the 50-set limit
 * for no purpose.
 */
const toSaved = (prefs: TickerPrefs): TickerPrefs => (prefs.allSets ? { ...prefs, recoverySetIds: [] } : { ...prefs, allSets: false })

export interface NotificationAuthor {
    id: number
    name: string
    initials: string
}

/** A list row: what the panel's rows and the ticker cards need. No body, no details. */
export interface NotificationRow {
    id: number
    recovery_set_id: number
    kind: TickerKind
    title: string
    summary: string
    /** ISO UTC */
    created_at: string
    object_id: string | null
    request_id: string | null
    author: NotificationAuthor | null
    /** A development sample from "Populate ticker", not a real event. Absent from older servers. */
    sample?: boolean
}

/** GET /api/notifications/:id */
export interface NotificationDetail extends NotificationRow {
    /** Sanitised HTML for engineer and management updates, plain text otherwise. */
    body: string | null
    details: Record<string, unknown>
    set_description: string | null
}

export interface NotificationPage {
    items: NotificationRow[]
    hasMore: boolean
}

export interface SearchPage extends NotificationPage {
    /** Matches across all pages. */
    total: number
}

export interface TickerRecoverySet {
    id: number
    description: string
    open: boolean
    /** Whether any ticker message belongs to it - the All messages filter offers these, closed or not. */
    hasMessages?: boolean
}

/** The thin socket event, `ticker:new`. Notice, not the record. */
export interface TickerNewEvent {
    id: number
    recovery_set_id: number
    kind: TickerKind
    title: string
    summary: string
    initials: string | null
    created_at: string
    /** Present, and true, only on a development sample. */
    sample?: boolean
}

/** GET /api/ticker/recovery-sets/:setId/samples */
export interface TickerSampleCount {
    count: number
}

const unwrap = <T,>(res: { data: unknown }): T => {
    const body = res.data as { data?: T } | T
    return ((body as { data?: T })?.data ?? body) as T
}

const setsParam = (sets: readonly number[]) => sets.join(',')

export const httpStatusOf = (error: unknown): number | undefined =>
    (error as { response?: { status?: number } } | null)?.response?.status

/**
 * Every /api/ticker/* and /api/notifications/* read requires `ticker.view` on the server. A 403
 * from any of them means the server did not agree, THIS time, that this user may see the ticker:
 * `onForbidden` (the store's markForbidden) hides the ticker and stops it asking. It is not a
 * final verdict. The server's role read answers 403 when it could not read the role at all (a
 * database blip), so a later success from any of them - `onAllowed` - brings the ticker back.
 * The preferences need only a sign-in, so they are not guarded.
 */
interface TickerAccessHandlers {
    onForbidden?: () => void
    onAllowed?: () => void
}
let access: TickerAccessHandlers = {}
export function setTickerAccessHandlers(handlers: TickerAccessHandlers) {
    access = handlers
}
const guarded = <T,>(request: Promise<T>): Promise<T> =>
    request.then(
        (value) => {
            access.onAllowed?.()
            return value
        },
        (error: unknown) => {
            if (httpStatusOf(error) === 403) access.onForbidden?.()
            throw error
        },
    )

export const tickerApi = {
    getPrefs: () => api.get('/api/me/preferences/ticker').then(unwrap<Partial<TickerPrefs>>).then(normalizePrefs),

    putPrefs: (prefs: TickerPrefs) =>
        api.put('/api/me/preferences/ticker', toSaved(prefs)).then(unwrap<Partial<TickerPrefs>>).then(normalizePrefs),

    /** Every named set, each with `open` (a set is closed once all its objects are complete). */
    recoverySets: () => guarded(api.get('/api/ticker/recovery-sets').then(unwrap<TickerRecoverySet[]>)),

    /** Backfill: ascending, id > after. */
    backfill: (sets: readonly number[], after: number, limit = 200) =>
        guarded(
            api
                .get('/api/ticker/notifications', { params: { sets: setsParam(sets), after, limit } })
                .then(unwrap<NotificationPage>),
        ),

    /** History: newest first; id < before when given. */
    history: (sets: readonly number[], opts: { before?: number; limit?: number } = {}) =>
        guarded(
            api
                .get('/api/ticker/notifications', {
                    params: { sets: setsParam(sets), limit: opts.limit ?? 50, ...(opts.before != null ? { before: opts.before } : {}) },
                })
                .then(unwrap<NotificationPage>),
        ),

    /**
     * The "N new messages" count: rows with id > after. With a `level`, only the kinds that level
     * admits (and rta_change, which every level admits) are counted: what the conveyor would show.
     */
    count: (sets: readonly number[], after: number, level?: TickerLevel) =>
        guarded(
            api
                .get('/api/ticker/notifications/count', { params: { sets: setsParam(sets), after, ...(level ? { level } : {}) } })
                .then(unwrap<{ count: number }>),
        ),

    /** All words must match; newest first. */
    search: (sets: readonly number[], q: string, opts: { before?: number; limit?: number } = {}) =>
        guarded(
            api
                .get('/api/ticker/notifications/search', {
                    params: { sets: setsParam(sets), q, limit: opts.limit ?? 50, ...(opts.before != null ? { before: opts.before } : {}) },
                })
                .then(unwrap<SearchPage>),
        ),

    notification: (id: number) => guarded(api.get(`/api/notifications/${id}`).then(unwrap<NotificationDetail>)),

    /*
     * Sample messages, development only (Andrew, 25 Sep 2026: "only available in development the
     * same as system templates"). They exist only where shipped content is authored; anywhere else
     * the server answers 404, which the caller reads as "not offered". Not `guarded`: writing needs
     * recover-requests.update, and a 403 for that says nothing about ticker.view.
     */
    sampleCount: (setId: number) => api.get(`/api/ticker/recovery-sets/${setId}/samples`).then(unwrap<TickerSampleCount>),

    /** One sample message of each kind for the set, stored and pushed like real ones. */
    populateSamples: (setId: number) =>
        api.post(`/api/ticker/recovery-sets/${setId}/samples`).then(unwrap<{ written: number; kinds: TickerKind[] }>),

    /** Delete the set's sample messages, and only those. */
    clearSamples: (setId: number) => api.delete(`/api/ticker/recovery-sets/${setId}/samples`).then(unwrap<{ deleted: number }>),
}
